/**
 * AddPermitModal — create or edit a Permit (Airtable).
 *
 * Opens via CustomEvent('open-add-permit-modal', { detail }):
 *   create: {}                       (project = current zoom project)
 *   edit:   { permitId, prefill: { typeOfPermit, subProjectName, trackingNumber, estimatedApproval } }
 *
 * On success busts ctx.permitCache and dispatches outline-render so PermitsSection reloads.
 */

import { useState, useEffect } from 'react'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'
import { createPermit, updatePermit, PERMIT_TYPES } from '../services/permits.js'
import Select from './Select.jsx'

const labelStyle = {
  display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10,
  color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase',
}
const inputStyle = {
  width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12,
  color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)',
  borderRadius: 4, boxSizing: 'border-box', outline: 'none',
}

export default function AddPermitModal() {
  const [open,       setOpen]       = useState(false)
  const [editId,     setEditId]     = useState(null)
  const [type,       setType]       = useState('')
  const [subName,    setSubName]    = useState('')
  const [tracking,   setTracking]   = useState('')
  const [estApproval,setEstApproval]= useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error,      setError]      = useState(null)

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      setEditId(d.permitId || null)
      setType(d.prefill?.typeOfPermit || '')
      setSubName(d.prefill?.subProjectName || '')
      setTracking(d.prefill?.trackingNumber || '')
      setEstApproval(d.prefill?.estimatedApproval || '')
      setError(null); setSubmitting(false); setOpen(true)
    }
    window.addEventListener('open-add-permit-modal', onOpen)
    return () => window.removeEventListener('open-add-permit-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  async function handleSubmit() {
    if (!type) return
    setSubmitting(true); setError(null)
    try {
      if (editId) {
        await updatePermit(editId, {
          'Type of Permit': type,
          'Sub Project Name': subName || '',
          'Permit Tracking Number': tracking || '',
          'Estimated Approval': estApproval || null,
        })
      } else {
        const projectId = ctx.recordId || wf.zoomProjectId
        if (!projectId) { setError('No project in context.'); setSubmitting(false); return }
        await createPermit(projectId, { typeOfPermit: type, subProjectName: subName, trackingNumber: tracking, estimatedApproval: estApproval || undefined })
      }
      ctx.permitCache = undefined
      setOpen(false)
      window.dispatchEvent(new Event('outline-render'))
    } catch (ex) {
      setError(ex.message || 'Failed to save permit'); setSubmitting(false)
    }
  }

  if (!open) return null
  const canSubmit = type && !submitting

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="AddPermitModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 420, padding: '20px 20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>
            {editId ? 'Edit Permit' : 'Add Permit'}
          </span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle}>Type of Permit <span style={{ color: '#c0392b' }}>*</span></label>
            <Select value={type} onChange={setType} options={PERMIT_TYPES.map(t => ({ value: t, label: t }))}
              placeholder="Select type…" searchable style={{ width: '100%', fontSize: 12 }} />
          </div>
          <div>
            <label style={labelStyle}>Sub Project Name</label>
            <input style={inputStyle} value={subName} onChange={e => setSubName(e.target.value)} placeholder="e.g. ADU, Pool, Second Story (optional)" />
          </div>
          <div>
            <label style={labelStyle}>Permit Tracking Number</label>
            <input style={inputStyle} value={tracking} onChange={e => setTracking(e.target.value)} placeholder="Agency tracking # (optional)" />
          </div>
          <div>
            <label style={labelStyle}>Estimated Approval</label>
            <input type="date" style={inputStyle} value={estApproval} onChange={e => setEstApproval(e.target.value)} />
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={!canSubmit} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: canSubmit ? '#07272D' : 'var(--aj-bg-muted)', color: canSubmit ? '#fff' : 'var(--aj-text-muted)', border: 'none', borderRadius: 4, cursor: canSubmit ? 'pointer' : 'default' }}>
              {submitting ? 'Saving…' : (editId ? 'Save' : 'Add Permit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
